/**
 * Shared GitHub API utilities
 *
 * Extracted from GithubRepoPicker, GithubTeamPicker, and GithubRepoVariablePicker
 * to provide a single, debugged codebase for GitHub API interactions.
 */

// --- Types ---

export interface GithubRepository {
  name: string;
  full_name: string;
  description: string | null;
  html_url: string;
  archived: boolean;
}

export interface GithubTeam {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  permission: string;
}

export interface RepoFetchOptions {
  topic?: string;
  pattern?: string;
  excludeArchived?: boolean;
}

// --- Pattern Matching ---

export function patternToRegex(pattern: string, caseInsensitive = false): RegExp {
  const escaped = pattern
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '.*')
    .replace(/\?/g, '.');
  return new RegExp(`^${escaped}$`, caseInsensitive ? 'i' : '');
}

// --- Paginated Fetching ---

export async function fetchAllPages<T>(
  url: string,
  token: string,
  perPage = 100,
): Promise<T[]> {
  const results: T[] = [];
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const separator = url.includes('?') ? '&' : '?';
    const response = await fetch(
      `${url}${separator}per_page=${perPage}&page=${page}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/vnd.github.v3+json',
        },
      },
    );

    if (!response.ok) {
      throw new Error(`GitHub API error: ${response.statusText}`);
    }

    const data: T[] = await response.json();
    results.push(...data);
    hasMore = data.length === perPage;
    page++;
  }

  return results;
}

// --- Repository Fetching ---

export async function fetchRepos(
  token: string,
  owner: string,
  options: RepoFetchOptions = {},
): Promise<GithubRepository[]> {
  const { topic, pattern, excludeArchived = true } = options;
  let repos: GithubRepository[];

  if (topic) {
    repos = await fetchReposByTopic(token, owner, topic, excludeArchived);
  } else {
    repos = await fetchAllPages<GithubRepository>(
      `https://api.github.com/orgs/${owner}/repos?sort=name`,
      token,
    );
  }

  if (excludeArchived) {
    repos = repos.filter(repo => !repo.archived);
  }

  if (pattern && pattern !== '*') {
    const regex = patternToRegex(pattern);
    repos = repos.filter(repo => regex.test(repo.name));
  }

  repos.sort((a, b) => a.name.localeCompare(b.name));
  return repos;
}

async function fetchReposByTopic(
  token: string,
  owner: string,
  topic: string,
  excludeArchived: boolean,
): Promise<GithubRepository[]> {
  const query = `topic:${topic} org:${owner}${excludeArchived ? ' archived:false' : ''}`;
  const repos: GithubRepository[] = [];
  let page = 1;
  const perPage = 100;
  let hasMore = true;

  while (hasMore) {
    const url = `https://api.github.com/search/repositories?q=${encodeURIComponent(query)}&per_page=${perPage}&page=${page}&sort=name&order=asc`;
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/vnd.github.v3+json',
      },
    });

    if (!response.ok) {
      throw new Error(`GitHub API error: ${response.statusText}`);
    }

    const data = await response.json();
    if (data.items) {
      repos.push(...data.items);
    }
    hasMore = data.items?.length === perPage && repos.length < data.total_count;
    page++;
  }

  return repos;
}

// --- Team Fetching ---

export async function fetchTeams(
  token: string,
  owner: string,
  pattern?: string,
): Promise<GithubTeam[]> {
  let teams = await fetchAllPages<GithubTeam>(
    `https://api.github.com/orgs/${owner}/teams`,
    token,
  );

  if (pattern && pattern !== '*') {
    const regex = patternToRegex(pattern, true);
    teams = teams.filter(team => regex.test(team.name) || regex.test(team.slug));
  }

  teams.sort((a, b) => a.name.localeCompare(b.name));
  return teams;
}

/**
 * Fetch teams for the authenticated user (not all org teams).
 * Uses GET /user/teams which returns teams the token owner belongs to.
 */
export async function fetchUserTeams(
  token: string,
  owner?: string,
): Promise<GithubTeam[]> {
  let teams = await fetchAllPages<GithubTeam & { organization: { login: string } }>(
    'https://api.github.com/user/teams',
    token,
  );

  // Filter to specific org if provided
  if (owner) {
    teams = teams.filter(
      team => team.organization?.login?.toLowerCase() === owner.toLowerCase(),
    );
  }

  teams.sort((a, b) => a.name.localeCompare(b.name));
  return teams;
}

// --- Repository Variable Fetching ---

export async function fetchRepoVariable(
  token: string,
  owner: string,
  repo: string,
  variableName: string,
  separator = ',',
): Promise<string[]> {
  const response = await fetch(
    `https://api.github.com/repos/${owner}/${repo}/actions/variables/${variableName}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/vnd.github.v3+json',
      },
    },
  );

  if (response.status === 404) {
    return [];
  }

  if (!response.ok) {
    throw new Error(`GitHub API error: ${response.statusText}`);
  }

  const data = await response.json();
  const value = data.value as string;

  return value
    .split(separator)
    .map((item: string) => item.trim())
    .filter((item: string) => item.length > 0);
}

// --- File Content Fetching ---

export async function fetchFileContent(
  token: string,
  owner: string,
  repo: string,
  path: string,
  ref?: string,
): Promise<string> {
  const refParam = ref ? `?ref=${encodeURIComponent(ref)}` : '';
  const response = await fetch(
    `https://api.github.com/repos/${owner}/${repo}/contents/${path}${refParam}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/vnd.github.v3.raw',
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch ${path}: ${response.statusText}`);
  }

  return response.text();
}
